import $api from '@/http/index';
import {
    IMatrixCellCommand,
    IMatrixCriterionCommand,
    IMatrixPositionCriterionCommand,
    IMatrixLotCommand,
    IWinnerMatrix,
    MatrixCommandResponse,
} from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection2/WinnerSelection2.types';

interface ICommandBody {
    matrix: IWinnerMatrix;
    warnings?: string[];
    message?: string;
    title?: string;
    errors?: Record<string, string[]>;
}

// Сохраняем структурированный ответ до общего перехватчика:
// 400 оставляет черновик, 409 обновляет матрицу для повторного решения.
const commandConfig = {
    validateStatus: (status: number) => (status >= 200 && status < 300) || status === 409 || status === 400,
    suppressErrorNotification: true,
};

const toResponse = (status: number, body: ICommandBody): MatrixCommandResponse => {
    if (status === 400)
        return {
            status: 'invalid',
            message:
                body.message ||
                Object.values(body.errors ?? {})
                    .flat()
                    .join(' ') ||
                body.title ||
                'Проверьте введённые данные.',
        };
    return status === 409
        ? {
              status: 'conflict',
              matrix: body.matrix,
              message: body.message ?? 'Данные изменились. Проверьте их и повторите.',
          }
        : { status: 'ok', matrix: body.matrix, warnings: body.warnings ?? [] };
};

export class WinnerSelection2Service {
    static async fetchMatrix(requestKey: string, tableKey: string, signal?: AbortSignal): Promise<IWinnerMatrix> {
        const response = await $api.get<IWinnerMatrix>(`/winnerSelection2/matrix/${requestKey}/${tableKey}`, {
            signal,
            suppressErrorNotification: true,
        } as object);
        return response.data;
    }

    static async saveCell(
        requestKey: string,
        tableKey: string,
        command: IMatrixCellCommand,
    ): Promise<MatrixCommandResponse> {
        const response = await $api.post<ICommandBody>(
            `/winnerSelection2/matrix/${requestKey}/${tableKey}/cell`,
            command,
            commandConfig as object,
        );
        return toResponse(response.status, response.data);
    }

    static async saveCriterion(
        requestKey: string,
        tableKey: string,
        command: IMatrixCriterionCommand,
    ): Promise<MatrixCommandResponse> {
        const response = await $api.post<ICommandBody>(
            `/winnerSelection2/matrix/${requestKey}/${tableKey}/criterion`,
            command,
            commandConfig as object,
        );
        return toResponse(response.status, response.data);
    }

    static async savePositionCriterion(
        requestKey: string,
        tableKey: string,
        command: IMatrixPositionCriterionCommand,
    ): Promise<MatrixCommandResponse> {
        const response = await $api.post<ICommandBody>(
            `/winnerSelection2/matrix/${requestKey}/${tableKey}/positionCriterion`,
            command,
            commandConfig as object,
        );
        return toResponse(response.status, response.data);
    }

    static async saveLot(
        requestKey: string,
        tableKey: string,
        command: IMatrixLotCommand,
    ): Promise<MatrixCommandResponse> {
        const response = await $api.post<ICommandBody>(
            `/winnerSelection2/matrix/${requestKey}/${tableKey}/lot`,
            command,
            commandConfig as object,
        );
        return toResponse(response.status, response.data);
    }
}
